// Sample content for the 1st Special Forces Group concept page.
// Everything here is placeholder copy meant to show what the unit could publish.
import fastRope from './Pictures/sfg/fast-rope.webp'
import aircraftLoading from './Pictures/sfg/aircraft-loading.webp'
import mudWall from './Pictures/sfg/mud-wall.webp'
import ammoTeam from './Pictures/sfg/ammo-team.webp'
import fieldLine from './Pictures/sfg/field-line.webp'
import binoculars from './Pictures/sfg/binoculars.webp'
import smokeAssault from './Pictures/sfg/smoke-assault.webp'
import familyDay from './Pictures/sfg/family-day.webp'
import shoreline from './Pictures/sfg/shoreline.webp'
import breach from './Pictures/sfg/breach.webp'
import winterRange from './Pictures/sfg/winter-range.webp'
import chinookPickup from './Pictures/sfg/chinook-pickup.webp'
import gunTeam from './Pictures/sfg/gun-team.webp'
import cqbLane from './Pictures/sfg/cqb-lane.webp'

export type Design = 'tactical' | 'editorial' | 'alpine'

export const designs: {id: Design, name: string, tagline: string, swatch: [string, string]}[] = [
    {id: 'tactical', name: 'Tactical', tagline: 'Black and gold operations board', swatch: ['#0b0d0a', '#e0b43a']},
    {id: 'editorial', name: 'Editorial', tagline: 'Photo-led commemorative journal', swatch: ['#f3eee4', '#2f4a3a']},
    {id: 'alpine', name: 'Alpine', tagline: 'Mountain field guide', swatch: ['#f4f7f9', '#e8501a']},
]

// The Alpine design reads the page as a climb up Mount Rainier, which overlooks JBLM.
// Each section is a waypoint on the standard summit route.
export const route = {
    base: {name: 'JBLM', elevation: 300},
    waypoints: [
        {number: '01', sectionId: 'sfg-board', name: 'Paradise', elevation: 5400},
        {number: '02', sectionId: 'sfg-ceremony', name: 'Camp Muir', elevation: 10188},
        {number: '03', sectionId: 'sfg-flyers', name: 'Ingraham Flats', elevation: 11100},
        {number: '04', sectionId: 'sfg-services', name: 'Summit', elevation: 14411},
    ],
}

export const formatFeet = (feet: number) => `${Math.round(feet).toLocaleString('en-US')} ft`

export const sectionCode = (design: Design, number: string, label: string) => {
    if (design === 'tactical') return `// ${number} · ${label}`
    if (design === 'editorial') return `No. ${number} · ${label}`
    const waypoint = route.waypoints.find(point => point.number === number)
    return waypoint ? `${waypoint.name} · ${formatFeet(waypoint.elevation)} · ${label}` : label
}

export const photos = {
    hero: {src: mudWall, alt: 'A Soldier aims his rifle from behind a sunlit mud wall'},
    story: {
        topLeft: {src: fieldLine, alt: 'A Soldier kneels behind a line of prone teammates in a dry field'},
        topRight: {src: fastRope, alt: 'Soldiers fast-rope from a helicopter through a cloud of dust'},
        bottomLeft: {src: smokeAssault, alt: 'Two Soldiers kneel in the grass while a team clears a building through smoke'},
        bottomRight: {src: aircraftLoading, alt: 'Paratroopers line up to board an aircraft under red cabin lights'},
    },
    strip: [
        {src: binoculars, alt: 'A Soldier scans the horizon through binoculars'},
        {src: ammoTeam, alt: 'A machine gun team feeds a belt of ammunition'},
        {src: breach, alt: 'A Soldier fires from the corner of a concrete wall while a teammate covers low'},
    ],
    alpineHero: {
        src: winterRange,
        alt: 'A sniper in winter camouflage aims a suppressed rifle from a plywood hide over a snowy mountain valley, with a drone controller beside him',
    },
    // Slides for the Alpine design's aperture carousel.
    fieldLog: [
        {image: chinookPickup, title: 'Exfil', caption: 'A team holds security in the rotor wash as the Chinook sets down.', alt: 'A line of Soldiers kneels in the dust as a Chinook helicopter lands behind them'},
        {image: fastRope, title: 'Fast rope', caption: 'Out of a hovering helicopter and straight into the dust.', alt: 'Soldiers fast-rope from a helicopter through a cloud of dust'},
        {image: gunTeam, title: 'Gun team', caption: 'A recoilless rifle crew settles in on the firing line.', alt: 'A Soldier shoulders a recoilless rifle while his teammate spots beside him'},
        {image: cqbLane, title: 'Close quarters', caption: 'A stack works its way through the urban training lane.', alt: 'A team of Soldiers advances with rifles raised through a narrow training street'},
        {image: mudWall, title: 'Overwatch', caption: 'Holding a corner of the compound wall at golden hour.', alt: 'A Soldier aims his rifle from behind a sunlit mud wall'},
        {image: aircraftLoading, title: 'Wheels up', caption: 'Chutes on, lined up under the red cabin lights.', alt: 'Paratroopers line up to board an aircraft under red cabin lights'},
    ],
}

export type Priority = 'FLASH' | 'PRIORITY' | 'ROUTINE'
export type Category = 'ANNOUNCEMENT' | 'CEREMONY' | 'EVENT' | 'FLYER' | 'DOCUMENT'

export interface IntelItem {
    id: string
    category: Category
    priority: Priority
    title: string
    date: string
    location?: string
    summary: string
    details: string[]
}

export interface Flyer {
    id: string
    kicker: string
    title: string
    subtitle: string
    date: string
    location: string
    details: string[]
    mark: string
    photo: string
    theme: 'gold' | 'olive' | 'red' | 'steel'
}

export interface Service {
    id: string
    code: string
    title: string
    pitch: string
    examples: string[]
}

export const CONTACT_EMAIL = 'jake@hitl-engineering.com'

export const company = {
    legalName: 'HITL Engineering LLC',
    registration: 'Registered Washington State LLC',
    insurance: ['General liability', 'Professional liability', 'Cyber liability'],
}

export const ceremony = {
    title: 'Change of Responsibility',
    unit: '1st Special Forces Group (Airborne)',
    outgoing: 'CSM [Outgoing Name]',
    incoming: 'CSM [Incoming Name]',
    hostedBy: 'Hosted by COL [Commander Name], Commanding',
    start: '2026-11-20T10:00:00-08:00',
    end: '2026-11-20T11:30:00-08:00',
    displayDate: '20 NOV 2026',
    displayTime: '1000 HRS',
    location: '[Parade Field], Joint Base Lewis-McChord, WA',
    attire: 'Uniform: Duty uniform with green beret · Civilian: Business casual',
    mapsUrl: 'https://maps.google.com/?q=Joint+Base+Lewis-McChord',
    checklist: [
        'No DoD ID? Request base access 72 hours out',
        'Plan 30 extra minutes at the visitor gate',
        'Be seated by 0945',
        'Reception at Group HQ right after',
    ],
}

export const intelItems: IntelItem[] = [
    {
        id: 'cor',
        category: 'CEREMONY',
        priority: 'FLASH',
        title: 'Group Change of Responsibility',
        date: '2026-11-20',
        location: '[Parade Field], JBLM',
        summary: 'The Group will bid farewell to its outgoing Command Sergeant Major and welcome the incoming. Families and guests are invited.',
        details: [
            'Guests should be seated by 0945. The ceremony begins at 1000 sharp.',
            'A reception follows at the Group headquarters immediately after the ceremony.',
            'Visitors without DoD ID: request base access at least 72 hours out.',
        ],
    },
    {
        id: 'leave',
        category: 'ANNOUNCEMENT',
        priority: 'PRIORITY',
        title: 'Holiday block leave schedule published',
        date: '2026-10-12',
        summary: 'Block leave windows for all battalions are now posted. Check with your company for your assigned window.',
        details: [
            'Window 1: 18 DEC - 27 DEC. Window 2: 28 DEC - 06 JAN.',
            'Submit leave forms through your chain of command NLT 20 NOV.',
        ],
    },
    {
        id: 'gate',
        category: 'ANNOUNCEMENT',
        priority: 'ROUTINE',
        title: 'Gate hours adjusted for ceremony week',
        date: '2026-10-10',
        summary: 'Expect extended processing times at the visitor center during the week of 16 NOV.',
        details: [
            'Plan an extra 30 minutes for gate access on ceremony day.',
            'Carpooling from the overflow lot is encouraged.',
        ],
    },
    {
        id: 'ruck',
        category: 'EVENT',
        priority: 'PRIORITY',
        title: 'Memorial ruck - honoring the fallen of First Group',
        date: '2026-11-07',
        location: 'Group HQ, JBLM',
        summary: 'A 12-mile memorial ruck open to Soldiers, families and alumni. Every mile is dedicated to a fallen teammate.',
        details: [
            'Step off at 0600. Recommended 35 lb ruck, lighter options welcome.',
            'Families can join the final mile at the memorial.',
        ],
    },
    {
        id: 'orgday',
        category: 'EVENT',
        priority: 'ROUTINE',
        title: 'Group Organization Day',
        date: '2026-12-04',
        location: 'Unit area, JBLM',
        summary: 'Team competitions, food trucks and a family zone. Battalion trophies are on the line.',
        details: [
            'Competitions run 0900 - 1400. Family zone opens at 1100.',
            'Sign teams up with your battalion S3 by 20 NOV.',
        ],
    },
    {
        id: 'party',
        category: 'FLYER',
        priority: 'ROUTINE',
        title: 'Family readiness holiday party',
        date: '2026-12-12',
        location: 'Community center, JBLM',
        summary: 'Santa, crafts and dinner for Soldiers and families. Sign up so we can plan food.',
        details: [
            '1600 - 1900. Bring an unwrapped toy for the toy drive.',
        ],
    },
    {
        id: 'seating',
        category: 'DOCUMENT',
        priority: 'ROUTINE',
        title: 'Ceremony seating and parking plan (PDF)',
        date: '2026-10-08',
        summary: 'Reserved seating, accessible parking and the shuttle loop for the Change of Responsibility.',
        details: [
            'Accessible parking is available next to the reviewing stand.',
        ],
    },
    {
        id: 'newcomer',
        category: 'DOCUMENT',
        priority: 'ROUTINE',
        title: 'Newcomer and in-processing guide (PDF)',
        date: '2026-09-30',
        summary: 'Everything a new arrival and their family need for the first 30 days at JBLM.',
        details: [
            'Includes housing, schools, sponsorship and key points of contact.',
        ],
    },
]

export const flyers: Flyer[] = [
    {
        id: 'ruck',
        kicker: 'Never forget',
        title: 'Memorial Ruck',
        subtitle: '12 miles · 12 names',
        date: 'SAT 07 NOV · 0600',
        location: 'Group HQ, JBLM',
        details: ['Open to Soldiers, families and alumni', 'Recommended 35 lb ruck', 'Families join the final mile'],
        mark: '07',
        photo: fieldLine,
        theme: 'red',
    },
    {
        id: 'orgday',
        kicker: 'First Group',
        title: 'Org Day',
        subtitle: 'Battalion vs battalion',
        date: 'FRI 04 DEC · 0900',
        location: 'Unit area, JBLM',
        details: ['Team competitions', 'Food trucks', 'Family zone from 1100'],
        mark: '04',
        photo: ammoTeam,
        theme: 'gold',
    },
    {
        id: 'party',
        kicker: 'Family readiness',
        title: 'Holiday Party',
        subtitle: 'Santa · crafts · dinner',
        date: 'SAT 12 DEC · 1600',
        location: 'Community center, JBLM',
        details: ['Bring an unwrapped toy', 'Kids crafts station', 'RSVP for food count'],
        mark: '12',
        photo: familyDay,
        theme: 'olive',
    },
    {
        id: 'ball',
        kicker: 'Formal',
        title: 'Group Ball',
        subtitle: 'An evening for the regiment of families',
        date: 'SAT 23 JAN · 1800',
        location: '[Venue], Tacoma, WA',
        details: ['Dress blues / evening attire', 'Dinner and dancing', 'Tickets via your company'],
        mark: '23',
        photo: shoreline,
        theme: 'steel',
    },
]

export const services: Service[] = [
    {
        id: 'ceremony',
        code: '01',
        title: 'Ceremony sites',
        pitch: 'Change of command and change of responsibility pages that replace the paper invite and the email chain.',
        examples: [
            'Digital invitation with RSVP and one-tap add-to-calendar',
            'Ceremony program and biographies guests can read on their phone',
            'Seating, parking and visitor gate access instructions',
            'Livestream link for family who cannot travel',
        ],
    },
    {
        id: 'board',
        code: '02',
        title: 'Announcement board',
        pitch: 'One always-current place for Group news, instead of announcements scattered across group chats.',
        examples: [
            'Staff post from a phone with no code',
            'FLASH items pinned to the top until they expire',
            'Searchable archive of past announcements',
        ],
    },
    {
        id: 'flyers',
        code: '03',
        title: 'Flyers that work',
        pitch: 'We design the flyer and the page behind it, so the print and the digital always match.',
        examples: [
            'Print-ready flyers for unit areas and FRG boards',
            'A QR code on every flyer that opens its event page',
            'Sign-up counts so planners know how much food to order',
        ],
    },
    {
        id: 'family',
        code: '04',
        title: 'Family readiness hub',
        pitch: 'A front door for spouses and families, including the ones who just arrived.',
        examples: [
            'Newcomer guide and PCS checklist',
            'Upcoming family events and FRG meeting dates',
            'Public resource links and points of contact',
        ],
    },
    {
        id: 'events',
        code: '05',
        title: 'Large-event logistics',
        pitch: 'Balls, memorials and reunions with the lodging, dining and schedule in one place.',
        examples: [
            'Timeline, venue maps and parking',
            'Lodging and dining recommendations',
            'Sponsor recognition pages',
            'Built like the 2nd Ranger Battalion 50th Anniversary pages on this site',
        ],
    },
    {
        id: 'sustain',
        code: '06',
        title: 'Hosting and sustainment',
        pitch: 'We keep it running so the S1 shop does not have to become web developers.',
        examples: [
            'Fast, secure hosting on a global network',
            'A weekly meeting and up to 4 hours of work every week',
            'Public-release content only, coordinated with your PAO',
            'Built mobile-first, because that is where families read it',
        ],
    },
]

export const phases = [
    {code: 'PH I', title: 'Recon', body: 'A short call with your S1, PAO and FRG leads. We inventory what you publish today and where it lives.'},
    {code: 'PH II', title: 'Build', body: 'We design and build a working draft you can click through on your own phone, then refine it with you.'},
    {code: 'PH III', title: 'Execute', body: 'Launch on your own address, with QR codes ready for the next flyer or invitation.'},
    {code: 'PH IV', title: 'Sustain', body: 'Text, call or send photos and we publish it. We meet every week to plan what is next. No tickets, no training required.'},
]

// Requested by 1st SFG: a 2-year base period, then up to 8 option years.
export const contractTerm = {
    baseYears: 2,
    optionYears: 8,
    meetingsPerWeek: 1,
    hoursPerWeek: 4,
}

// What HITL Engineering commits to during the base period.
export const baseCommitments = [
    {
        id: 'managed',
        title: 'Fully managed',
        lead: 'Everything about the website is handled for you.',
        items: [
            'Content updates, new pages, announcements and events',
            'Domain, DNS and hosting',
            'Security: HTTPS, patches, dependency updates and monitoring',
            'Automatic backups with one-step restore',
            'Uptime monitoring with alerts',
            'Accessibility to Section 508 and WCAG 2.1 AA',
            'Photo editing and resizing so pages stay fast on phones',
            'Print-ready flyers with QR codes that open the matching page',
        ],
    },
    {
        id: 'weekly',
        title: 'Every week',
        lead: 'A standing rhythm, so nothing waits on a ticket queue.',
        items: [
            'A weekly meeting at the time that suits the Group',
            'Up to 4 hours of hands-on work every week',
            'A short written recap of what changed after each meeting',
            'Planning ahead for ceremonies, balls and changes of command',
            'Content flagged for PAO review before anything goes public',
        ],
    },
    {
        id: 'yours-to-do',
        title: 'All you do',
        lead: 'Send it whatever way is easiest.',
        items: [
            'Call or text any time with what you need',
            'Send photos straight from your phone',
            'Forward a flyer, a memo or a rough note and it becomes a page',
            'No logins, tickets or training required',
        ],
    },
    {
        id: 'ownership',
        title: 'You own all of it',
        lead: 'No lock-in, from day one.',
        items: [
            'Domain registered to the Group',
            'Full source code in a repository the Group controls',
            'Admin access to hosting, domain and analytics',
            'A written handover guide, kept current throughout',
            'Ready to hand over at any time, to anyone you choose',
        ],
    },
]

// The Group decides how the option years run. HITL Engineering's preference is to keep managing it.
export const optionPaths = [
    {
        id: 'managed',
        tag: 'Our preference',
        title: 'Keep it fully managed',
        body: 'We would like to stay on for the whole contract, through year 10.',
        items: [
            'Everything from the base period continues',
            'Weekly meeting and up to 4 hours of work every week',
            'Updates, domain, hosting and security stay handled',
        ],
    },
    {
        id: 'in-house',
        tag: 'Always an option',
        title: 'Take it in-house',
        body: 'It is already yours, so taking it over is simple.',
        items: [
            'We hand over the code, domain, accounts and the handover guide',
            'A walkthrough with whoever takes it over',
            'No fees, no lock-in and no hard feelings',
        ],
    },
]
